import type { ApolloClient } from '@apollo/client';

import type { UserData } from 'js/lib/user';

import SettingsDispatcher from 'bundles/account-settings/SettingsDispatcher';
import SettingsServerActionCreators from 'bundles/account-settings/actions/SettingsServerActionCreators';
import SettingsConstants from 'bundles/account-settings/constants/SettingsConstants';
import SettingsAPIUtils from 'bundles/account-settings/utils/SettingsAPIUtils';

const { Actions, Sections } = SettingsConstants;

type SECTION_VALUE_TYPES = (typeof SettingsConstants)['Sections'][keyof (typeof SettingsConstants)['Sections']];
type TOP_SECTION_VALUE_TYPES =
  (typeof SettingsConstants)['TopSections'][keyof (typeof SettingsConstants)['TopSections']];

const exported = {
  setUserData(userData: UserData) {
    SettingsServerActionCreators.receivedUserSettings(userData);
  },

  /**
   * Sets top-level section to display
   */
  setTopSection(section: TOP_SECTION_VALUE_TYPES) {
    SettingsDispatcher.handleViewAction({
      type: Actions.SET_TOP_SECTION,
      section,
    });
  },

  /**
   * Get current user settings
   */
  getUserSettings(userId: number, client: ApolloClient<unknown>) {
    SettingsAPIUtils.getUserSettings(userId, client);
  },

  fetchPaymentWallets(userId: number, client?: ApolloClient<unknown>) {
    return SettingsAPIUtils.fetchPaymentWallets(userId, client);
  },

  /**
   * Get current user settings
   */
  fetchCreditCardPreferences(userId: number) {
    return SettingsAPIUtils.fetchCreditCardPreferences(userId);
  },

  refetchPaymentData(userId: number, client?: ApolloClient<unknown>) {
    return Promise.all([this.fetchCreditCardPreferences(userId), this.fetchPaymentWallets(userId, client)]);
  },

  updateEmailAddressWithPassword(email: string, password: string, userId: number) {
    this.resetSection(Sections.BASIC_INFORMATION);
    return SettingsAPIUtils.updateEmailAddressWithPassword(email, password, userId);
  },

  /**
   * Submits name change request
   * @param {Object} nameChangeData
   */
  submitNameChangeRequest(nameChangeData: $TSFixMe) {
    return SettingsAPIUtils.submitNameChangeRequest(nameChangeData);
  },

  /**
   * Updates user passwords
   * @param {Object} passwords
   */
  resetPassword(passwords: $TSFixMe) {
    this.resetSection(Sections.PASSWORD);
    SettingsAPIUtils.resetPassword(passwords);
  },

  requestResetPassword() {
    return SettingsAPIUtils.requestResetPassword();
  },

  addAlternativeEmail(email: string) {
    return SettingsAPIUtils.addAlternativeEmail(email);
  },

  sendVerificationEmail(email: string) {
    return SettingsAPIUtils.sendVerificationEmail(email);
  },

  showDeleteCreditCardModal(walletId: $TSFixMe) {
    this.resetSection(Sections.CREDIT_CARD_DELETE);
    SettingsDispatcher.handleViewAction({
      type: Actions.SHOW_DELETE_CREDIT_CARD_MODAL,
      walletId,
    });
  },

  hideDeleteCreditCardModal() {
    SettingsDispatcher.handleViewAction({
      type: Actions.HIDE_DELETE_CREDIT_CARD_MODAL,
    });
  },

  deleteCreditCard(walletId: $TSFixMe) {
    this.hideDeleteCreditCardModal();
    this.resetSection(Sections.CREDIT_CARD_DELETE);
    return SettingsAPIUtils.deleteCreditCard(walletId);
  },

  /**
   * Updates user email preferences
   * @param {String} section
   * @param {Object} preferences
   */
  updateEmailPreferences(section: SECTION_VALUE_TYPES, preferences: $TSFixMe, client: ApolloClient<unknown>) {
    this.resetSection(section);
    SettingsAPIUtils.updateEmailPreferences(section, preferences, client);
  },

  togglePartnerEmailSubscription(
    partnerId: $TSFixMe,
    courseId: $TSFixMe,
    subscribe: $TSFixMe,
    client: ApolloClient<unknown>
  ) {
    SettingsAPIUtils.togglePartnerEmailSubscription(partnerId, courseId, subscribe, client);
  },

  getPartnerBannerViewStatus(courseId: $TSFixMe, partnerId: $TSFixMe) {
    SettingsAPIUtils.checkPartnerBannerStatus(courseId, partnerId);
  },

  dismissPartnerEmailSubscription(courseId: $TSFixMe, partnerId: $TSFixMe) {
    SettingsAPIUtils.dismissPartnerBanner(courseId, partnerId, false);
  },

  unsubscribeAllEmails() {
    SettingsAPIUtils.unsubscribeAllEmails();
  },

  resetSection(section: SECTION_VALUE_TYPES) {
    SettingsDispatcher.handleViewAction({
      type: Actions.RESET_SECTION,
      section,
    });
  },

  resetAllSections() {
    SettingsDispatcher.handleViewAction({
      type: Actions.RESET_ALL_SECTIONS,
    });
  },

  updatePendingChangesStatus(section: SECTION_VALUE_TYPES, isChanged: $TSFixMe) {
    SettingsDispatcher.handleViewAction({
      type: Actions.UPDATE_PENDING_CHANGES_STATUS,
      section,
      isChanged,
    });
  },

  /**
   * Request to load Two-Factor Authentication settings from the server
   */
  loadTwoFactorAuthenticationSettings() {
    SettingsAPIUtils.loadTwoFactorAuthenticationSettings();
  },

  /**
   * Initiate the Two-Factor Authentication setup process
   */
  enableTwoFactorAuthentication() {
    SettingsDispatcher.handleViewAction({
      type: Actions.ENABLE_TWO_FACTOR_AUTHENTICATION,
    });
  },

  /**
   * Confirm a user's password before enabling Two-Factor Authentication
   * @param {String} password
   */
  confirmPasswordToEnableTwoFactorAuthentication(password: $TSFixMe) {
    SettingsAPIUtils.confirmPasswordToEnableTwoFactorAuthentication(password);
  },

  /**
   * Confirm a user's Two-Factor code before enabling Two-Factor authentication
   * @param {String} code
   */
  confirmTwoFactorAuthenticationCode(code: $TSFixMe) {
    SettingsAPIUtils.confirmTwoFactorAuthenticationCode(code);
  },

  /**
   * Initiate the Two-Factor Authentication disabling process
   */
  disableTwoFactorAuthentication() {
    SettingsDispatcher.handleViewAction({
      type: Actions.DISABLE_TWO_FACTOR_AUTHENTICATION,
    });
  },

  /**
   * Confirm a user's password before disabling Two-Factor Authentication
   * @param {String} password
   */
  confirmPasswordToDisableTwoFactorAuthentication(password: $TSFixMe) {
    SettingsAPIUtils.confirmPasswordToDisableTwoFactorAuthentication(password);
  },

  /**
   * Cancel any Two-Factor Authentication process (either setup or disabling)
   */
  cancelTwoFactorAuthenticationAction() {
    SettingsDispatcher.handleViewAction({
      type: Actions.CANCEL_TWO_FACTOR_AUTHENTICATION_ACTION,
    });
  },

  /**
   * Finally after account is deleted call the logout API
   */
  accountDeleted() {
    SettingsAPIUtils.logout();
  },
};

export default exported;

export const {
  setUserData,
  setTopSection,
  getUserSettings,
  fetchCreditCardPreferences,
  fetchPaymentWallets,
  updateEmailAddressWithPassword,
  submitNameChangeRequest,
  resetPassword,
  requestResetPassword,
  addAlternativeEmail,
  showDeleteCreditCardModal,
  hideDeleteCreditCardModal,
  deleteCreditCard,
  updateEmailPreferences,
  togglePartnerEmailSubscription,
  getPartnerBannerViewStatus,
  dismissPartnerEmailSubscription,
  unsubscribeAllEmails,
  resetSection,
  resetAllSections,
  updatePendingChangesStatus,
  loadTwoFactorAuthenticationSettings,
  enableTwoFactorAuthentication,
  confirmPasswordToEnableTwoFactorAuthentication,
  confirmTwoFactorAuthenticationCode,
  disableTwoFactorAuthentication,
  confirmPasswordToDisableTwoFactorAuthentication,
  cancelTwoFactorAuthenticationAction,
  accountDeleted,
} = exported;
